import React, { useState, useMemo, useEffect } from "react";
import { Box, Button, MenuItem, Select, FormControl, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { axios_tenant } from "../../../axios";
import { ENTITY_STATUS_OPTIONS, matchesEntityStatusFilter } from "./utils/entityStatus";
import { formatKotDate } from "./utils/dateHelpers";
import TruncatedText from "./components/TruncatedText";

const InactiveClients = () => {
  const [clients, setClients] = useState([]);
  const [entityFilter, setEntityFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const fetchClients = async () => {
    try {
      setLoading(true);

      const response = await axios_tenant.get("/kot/client-profiles", {
        params: { status: "INACTIVE" },
      });

      let data = response?.data?.content || [];

      setClients(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const clientName = client.clientName || client.clientLegalName || client.name || "";

      const matchesEntity = matchesEntityStatusFilter(client.entityStatus, entityFilter);

      const matchesClient = clientFilter === "" || clientName === clientFilter;

      return matchesEntity && matchesClient;
    });
  }, [clients, entityFilter, clientFilter]);

  const viewProfile = (id) => {
    navigate(`/kot/client/edit/${id}`);
  };

  const columns = [
    {
      field: "clientName",
      headerName: "Client Name",
      flex: 1,
      renderCell: (params) => <TruncatedText text={params.row.clientName || params.row.clientLegalName || params.row.name || "-"} maxLength={30} />,
    },
    { field: "deactivatedBy", headerName: "Deactivated By", flex: 1, renderCell: (params) => <span>{params.row.lastUpdatedBy.displayName}</span> },
    {
      field: "deactivatedOn",
      headerName: "Deactivated On",
      flex: 1,
      renderCell: (params) => <span>{formatKotDate(params.row.lastUpdatedAt)}</span>,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button onClick={() => viewProfile(params.row.id)} sx={{ textAlign: "center" }}>
          View Profile
        </Button>
      ),
    },
  ];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h3" alignSelf={"center"}>
          Inactive Clients
        </Typography>

        <Box display="flex" gap={2}>
          <FormControl size="small">
            <Select displayEmpty value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <MenuItem value="">Client Name</MenuItem>
              {[...new Set(clients.map((c) => c.clientName || c.clientLegalName || c.name).filter(Boolean))].map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <Select displayEmpty value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
              <MenuItem value="">Entity Status</MenuItem>
              {ENTITY_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          height: 700,
          width: "100%",
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 2,
        }}
      >
        <DataGrid
          sx={{
            border: 0,

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: "bold",
              borderBottom: "1px solid #ddd",
            },

            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #eee",
              overflow: "hidden",
            },
            "& .MuiDataGrid-cell > span": {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            },

            "& .MuiDataGrid-columnSeparator": {
              visibility: "visible",
            },
            // Custom pagination styles
            ".MuiTablePagination-displayedRows": {
              marginTop: "1em",
              marginBottom: "1em",
            },
            ".MuiTablePagination-selectLabel": {
              marginTop: "1em",
              marginBottom: "1em",
            },
          }}
          rows={filteredClients}
          columns={columns}
          disableRowSelectionOnClick
          pageSize={8}
          disableColumnFilter
          disableColumnMenu
          rowsPerPageOptions={[8]}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default InactiveClients;
